import api from './api';

export interface SettingValue {
  value: string;
  description?: string;
  updatedAt: string;
}

export interface Settings {
  [key: string]: SettingValue;
}

export interface SettingsResponse {
  success: boolean;
  data: {
    settings: Settings;
    lastSync: {
      syncType: string;
      recordsProcessed: number;
      completedAt: string;
    } | null;
  };
}

export interface TestConnectionResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    spreadsheetId: string;
    title: string;
    sheetCount: number;
    sheets: string[];
    url: string;
  };
}

export interface SyncStatusResponse {
  success: boolean;
  data: {
    isRunning: boolean;
    currentSync: any | null;
    recentSyncs: any[];
  };
}

export interface DatabaseHealthResponse {
  success: boolean;
  data: {
    status: 'healthy' | 'warning' | 'error';
    connection: 'connected' | 'disconnected';
    tables: {
      [tableName: string]: {
        exists: boolean;
        recordCount?: number;
        status: 'ok' | 'missing';
        error?: string;
      };
    };
    issues: string[];
    missingSettings?: string[];
    settingsCount?: number;
  };
}

/**
 * Fetch all settings
 */
export const getSettings = async (): Promise<SettingsResponse> => {
  const response = await api.get<SettingsResponse>('/settings');
  return response.data;
};

/**
 * Update settings
 */
export const updateSettings = async (settings: Record<string, string>): Promise<any> => {
  const response = await api.put('/settings', { settings });
  return response.data;
};

/**
 * Test Google Sheets connection
 */
export const testConnection = async (spreadsheetId: string): Promise<TestConnectionResponse> => {
  const response = await api.post<TestConnectionResponse>('/settings/test-connection', {
    spreadsheetId,
  });
  return response.data;
};

/**
 * Trigger immediate sync
 */
export const syncNow = async (): Promise<any> => {
  const response = await api.post('/settings/sync-now');
  return response.data;
};

/**
 * Get sync status
 */
export const getSyncStatus = async (): Promise<SyncStatusResponse> => {
  const response = await api.get<SyncStatusResponse>('/settings/sync-status');
  return response.data;
};

/**
 * Check database health
 */
export const checkDatabaseHealth = async (): Promise<DatabaseHealthResponse> => {
  const response = await api.get<DatabaseHealthResponse>('/settings/database-health');
  return response.data;
};

const settingsService = {
  getSettings,
  updateSettings,
  testConnection,
  syncNow,
  getSyncStatus,
  checkDatabaseHealth,
};

export default settingsService;
